import { z } from 'zod';
/**
 * Envíos de SGDEA (GUIA-AGENTES §3.10 de la suite): cada tipo de correo que manda la app. Se publica en la suite al
 * arrancar (`SuiteClient.publishMailFeatures`) y la entidad elige allá, envío por envío, por dónde sale: un correo
 * saliente, el certificado (solo los `certifiable`) o «No enviar». Todo correo que sale por `MailSenderService` lleva
 * uno de estos códigos; nunca se envía sin él.
 */
export const MailFeatureCode = {
    // Al ciudadano
    RADICACION_CONFIRMACION: 'radicacion.confirmacion',
    INFORMACION_SOLICITUD: 'informacion.solicitud',
    RESPUESTA: 'respuesta',
    RESPUESTA_FELICITACION: 'respuesta.felicitacion',
    CIERRE: 'cierre',
    CASO_TRAMITE: 'caso.tramite',
    CIUDADANO_MANUAL: 'ciudadano.manual',
    // Al juzgado
    JUZGADO_COMUNICACION: 'juzgado.comunicacion',
    // A funcionarios
    TAREAS_NUEVA: 'tareas.nueva',
    TAREAS_REASIGNADA: 'tareas.reasignada',
    TAREAS_SIN_RESPONSABLE: 'tareas.sin_responsable',
    CASOS_ASIGNADO: 'casos.asignado',
    CASOS_SIN_ASIGNAR: 'casos.sin_asignar',
    CASOS_ESCALAMIENTO: 'casos.escalamiento',
    TERMINOS_POR_VENCER: 'terminos.por_vencer',
    TERMINOS_ESCALAMIENTO: 'terminos.escalamiento',
    TERMINOS_VENCIDO: 'terminos.vencido',
    CIUDADANO_RESPUESTA: 'ciudadano.respuesta',
    JUZGADO_RESPUESTA: 'juzgado.respuesta',
    APOYO_SOLICITUD: 'apoyo.solicitud',
    APOYO_RESPUESTA: 'apoyo.respuesta',
    TRASLADO_SOLICITUD: 'traslado.solicitud',
    TRASLADO_DECISION: 'traslado.decision',
    REAPERTURA_SOLICITUD: 'reapertura.solicitud',
    REAPERTURA_DECISION: 'reapertura.decision',
    PROCESO_AVISO: 'proceso.aviso',
    FIRMA_CODIGO: 'firma.codigo',
    PUESTA_EN_MARCHA: 'puesta_en_marcha',
};
const CIUDADANO = 'Ciudadano';
const JUZGADO = 'Juzgado';
const TAREAS = 'Tareas y casos';
const TERMINOS = 'Términos';
const OTROS = 'Otros';
/** El catálogo que SGDEA publica en la suite (`mailFeatureSchema` de `@xiltrion/correo`), en el orden de la consola. */
export const SGDEA_MAIL_FEATURES = [
    { code: MailFeatureCode.RADICACION_CONFIRMACION, group: CIUDADANO, certifiable: true, name: 'Confirmación de radicación', description: 'Acuse de recibo con el número de radicado (plantilla «Caso recibido»).' },
    { code: MailFeatureCode.INFORMACION_SOLICITUD, group: CIUDADANO, certifiable: true, name: 'Solicitud de información', description: 'Le pide al ciudadano los datos o documentos que faltan para seguir el trámite.' },
    { code: MailFeatureCode.RESPUESTA, group: CIUDADANO, certifiable: true, name: 'Respuesta', description: 'Respuesta de fondo con términos legales: petición, queja, reclamo, denuncia y demás.' },
    { code: MailFeatureCode.RESPUESTA_FELICITACION, group: CIUDADANO, certifiable: true, name: 'Respuesta a felicitación o sugerencia', description: 'Respuesta a una felicitación o una sugerencia.' },
    { code: MailFeatureCode.CIERRE, group: CIUDADANO, certifiable: true, name: 'Cierre del caso', description: 'Le informa al ciudadano que su solicitud se cerró.' },
    { code: MailFeatureCode.CASO_TRAMITE, group: CIUDADANO, certifiable: true, name: 'Novedades del trámite', description: 'Otros avisos del proceso al ciudadano, como que su solicitud ya está en trámite.' },
    { code: MailFeatureCode.CIUDADANO_MANUAL, group: CIUDADANO, certifiable: false, name: 'Mensaje escrito por un funcionario', description: 'Lo que un funcionario le escribe al ciudadano desde la pestaña «Comunicaciones» del caso.' },
    { code: MailFeatureCode.JUZGADO_COMUNICACION, group: JUZGADO, certifiable: true, name: 'Comunicación al juzgado', description: 'Contestación, impugnación o informe de cumplimiento al despacho judicial de un requerimiento.' },
    { code: MailFeatureCode.TAREAS_NUEVA, group: TAREAS, certifiable: false, name: 'Tarea nueva', description: 'Aviso al funcionario que recibe una tarea.' },
    { code: MailFeatureCode.TAREAS_REASIGNADA, group: TAREAS, certifiable: false, name: 'Tarea reasignada', description: 'Aviso a quien le reasignan una tarea.' },
    { code: MailFeatureCode.TAREAS_SIN_RESPONSABLE, group: TAREAS, certifiable: false, name: 'Tarea sin responsable', description: 'Aviso a la cadena de mando cuando nadie puede tomar una tarea.' },
    { code: MailFeatureCode.CASOS_ASIGNADO, group: TAREAS, certifiable: false, name: 'Caso asignado', description: 'Aviso a quien queda como responsable de un caso, también al reasignarlo.' },
    { code: MailFeatureCode.CASOS_SIN_ASIGNAR, group: TAREAS, certifiable: false, name: 'Caso sin asignar', description: 'Aviso a la cadena de mando cuando un caso no se pudo asignar solo.' },
    { code: MailFeatureCode.CASOS_ESCALAMIENTO, group: TAREAS, certifiable: false, name: 'Caso escalado', description: 'Aviso al superior al que se escala un caso.' },
    { code: MailFeatureCode.TERMINOS_POR_VENCER, group: TERMINOS, certifiable: false, name: 'Plazo por vencer', description: 'Aviso al responsable cuando el plazo de un caso está por vencer.' },
    { code: MailFeatureCode.TERMINOS_ESCALAMIENTO, group: TERMINOS, certifiable: false, name: 'Plazo en riesgo a la cadena de mando', description: 'Aviso a la cadena de mando de un plazo por vencer o vencido.' },
    { code: MailFeatureCode.TERMINOS_VENCIDO, group: TERMINOS, certifiable: false, name: 'Plazo vencido', description: 'Aviso al responsable cuando el plazo de un caso venció.' },
    { code: MailFeatureCode.CIUDADANO_RESPUESTA, group: OTROS, certifiable: false, name: 'El ciudadano respondió', description: 'Aviso al responsable cuando el ciudadano responde un correo del caso.' },
    { code: MailFeatureCode.JUZGADO_RESPUESTA, group: OTROS, certifiable: false, name: 'El juzgado escribió', description: 'Aviso cuando el despacho judicial escribe en un requerimiento.' },
    { code: MailFeatureCode.APOYO_SOLICITUD, group: OTROS, certifiable: false, name: 'Solicitud de apoyo', description: 'Aviso al área a la que le piden apoyo en un caso, o que se canceló la solicitud.' },
    { code: MailFeatureCode.APOYO_RESPUESTA, group: OTROS, certifiable: false, name: 'Respuesta de apoyo', description: 'Aviso a quien pidió apoyo cuando el área responde.' },
    { code: MailFeatureCode.TRASLADO_SOLICITUD, group: OTROS, certifiable: false, name: 'Solicitud de traslado de área', description: 'Aviso a quien aprueba en el área cuando le piden trasladar un caso a otra, o que se canceló la solicitud.' },
    { code: MailFeatureCode.TRASLADO_DECISION, group: OTROS, certifiable: false, name: 'Decisión de traslado de área', description: 'Aviso a quien pidió el traslado de un caso cuando se aprueba o se rechaza.' },
    { code: MailFeatureCode.REAPERTURA_SOLICITUD, group: OTROS, certifiable: false, name: 'Solicitud de reapertura', description: 'Aviso a quien aprueba en el área cuando el responsable pide reabrir un caso cerrado para una respuesta complementaria, o que se canceló la solicitud.' },
    { code: MailFeatureCode.REAPERTURA_DECISION, group: OTROS, certifiable: false, name: 'Decisión de reapertura', description: 'Aviso a quien pidió reabrir un caso cuando se aprueba o se rechaza.' },
    { code: MailFeatureCode.PROCESO_AVISO, group: OTROS, certifiable: false, name: 'Aviso del proceso', description: 'Avisos que el flujo de un caso envía a funcionarios (paso «Enviar notificación» y otros).' },
    { code: MailFeatureCode.FIRMA_CODIGO, group: OTROS, certifiable: false, name: 'Código para firmar', description: 'Código de un solo uso para firmar una respuesta.' },
    { code: MailFeatureCode.PUESTA_EN_MARCHA, group: OTROS, certifiable: false, name: 'Revisión de la configuración', description: 'Aviso a los administradores cuando la revisión diaria encuentra configuración por atender.' },
];
const CERTIFIABLE = new Set(SGDEA_MAIL_FEATURES.filter((f) => f.certifiable).map((f) => f.code));
/** ¿El envío se puede mandar con certificado? (Solo los correos al ciudadano o al despacho judicial de un expediente.) */
export function isCertifiableFeature(code) {
    return CERTIFIABLE.has(code);
}
/**
 * Tipos de caso cuya respuesta es `respuesta.felicitacion` (sin los términos legales de una petición, queja o reclamo):
 * los de la clasificación base. Un tipo propio cuenta igual si su respuesta usa la plantilla `RESPONSE_FELICITACION`.
 */
export const FELICITATION_CASE_TYPES = ['FELICITACION', 'SUGERENCIA'];
export const FELICITATION_TEMPLATE = 'RESPONSE_FELICITACION';
/**
 * El envío de un correo al ciudadano según su plantilla (`notification_templates`): `NOTIFY_RECEIVED` es la
 * confirmación de radicación; las de clase `INFORMATION_REQUEST`, `CLOSURE` y `RESPONSE` son la solicitud de
 * información, el cierre y la respuesta (de felicitación o sugerencia según el tipo de caso o la plantilla); lo demás,
 * novedades del trámite.
 */
export function citizenMailFeature(t) {
    if (t.templateCode === 'NOTIFY_RECEIVED')
        return MailFeatureCode.RADICACION_CONFIRMACION;
    if (t.templateCode === 'NOTIFY_INFO_REQUESTED' || t.templateKind === 'INFORMATION_REQUEST')
        return MailFeatureCode.INFORMACION_SOLICITUD;
    if (t.templateKind === 'CLOSURE')
        return MailFeatureCode.CIERRE;
    if (t.templateKind === 'RESPONSE') {
        const felicitation = t.templateCode === FELICITATION_TEMPLATE || (!!t.caseTypeCode && FELICITATION_CASE_TYPES.includes(t.caseTypeCode));
        return felicitation ? MailFeatureCode.RESPUESTA_FELICITACION : MailFeatureCode.RESPUESTA;
    }
    return MailFeatureCode.CASO_TRAMITE;
}
/** El envío del correo de cada tipo de aviso a funcionarios (`NotificationsService.notifyUser`). */
const NOTIFICATION_FEATURE = {
    NEW_TASK: MailFeatureCode.TAREAS_NUEVA,
    TASK_REASSIGNED: MailFeatureCode.TAREAS_REASIGNADA,
    UNASSIGNED_TASK: MailFeatureCode.TAREAS_SIN_RESPONSABLE,
    CASE_ASSIGNED: MailFeatureCode.CASOS_ASIGNADO,
    CASE_REASSIGNED: MailFeatureCode.CASOS_ASIGNADO,
    UNASSIGNED_CASE: MailFeatureCode.CASOS_SIN_ASIGNAR,
    ESCALATION: MailFeatureCode.CASOS_ESCALAMIENTO,
    SLA_WARNING: MailFeatureCode.TERMINOS_POR_VENCER,
    SLA_ESCALATION: MailFeatureCode.TERMINOS_ESCALAMIENTO,
    SLA_BREACH: MailFeatureCode.TERMINOS_VENCIDO,
    CITIZEN_REPLY: MailFeatureCode.CIUDADANO_RESPUESTA,
    COURT_REPLY: MailFeatureCode.JUZGADO_RESPUESTA,
    SUPPORT_REQUESTED: MailFeatureCode.APOYO_SOLICITUD,
    SUPPORT_CANCELLED: MailFeatureCode.APOYO_SOLICITUD,
    SUPPORT_ANSWERED: MailFeatureCode.APOYO_RESPUESTA,
    AREA_TRANSFER_REQUESTED: MailFeatureCode.TRASLADO_SOLICITUD,
    AREA_TRANSFER_CANCELLED: MailFeatureCode.TRASLADO_SOLICITUD,
    AREA_TRANSFER_DECIDED: MailFeatureCode.TRASLADO_DECISION,
    REOPEN_REQUESTED: MailFeatureCode.REAPERTURA_SOLICITUD,
    REOPEN_CANCELLED: MailFeatureCode.REAPERTURA_SOLICITUD,
    REOPEN_DECIDED: MailFeatureCode.REAPERTURA_DECISION,
    WORKFLOW: MailFeatureCode.PROCESO_AVISO,
    SETUP_ATTENTION: MailFeatureCode.PUESTA_EN_MARCHA,
};
/** El envío de un aviso a funcionarios; un tipo sin envío propio sale como aviso del proceso. */
export function notificationMailFeature(type) {
    return NOTIFICATION_FEATURE[type] ?? MailFeatureCode.PROCESO_AVISO;
}
// ─────────────────────────────────────────── Envíos de los pasos de los flujos
/**
 * Pasos de flujo que le escriben al ciudadano o al juzgado: cada uno es un envío propio de la entidad
 * (`SuiteClient.publishEntityMailFeatures`), que hereda la ruta de su categoría (`parent`, un envío de la lista fija)
 * mientras la entidad no le elija una.
 */
export const MAIL_STEP_ACTIONS = ['NOTIFY_CITIZEN', 'REQUEST_INFORMATION', 'SEND_TO_COURT'];
/** Plantilla que manda «Notificar al ciudadano» cuando el paso no nombra ninguna (`WorkflowRuntimeService`). */
export const DEFAULT_NOTIFY_TEMPLATE = 'NOTIFY_CLOSED';
/** ¿El paso manda un correo (y es un envío propio)? */
export function sendsMail(n) {
    return (n.type === 'system_task' || n.type === 'human_task') && MAIL_STEP_ACTIONS.includes(n.actionCode ?? '');
}
/** Lo que vale en un código de envío, en minúsculas; lo demás pasa a guion. */
const codePart = (v) => v.toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'x';
/**
 * Código estable del envío de un paso: `flujo.<código del flujo>.<id del paso>` en minúsculas. El código del flujo y el
 * id del paso no cambian, así que la ruta que la entidad le elija sigue valiendo en las versiones siguientes.
 */
export function stepMailFeatureCode(workflowCode, nodeId) {
    const code = `flujo.${codePart(workflowCode)}.${codePart(nodeId)}`;
    if (code.length <= 80)
        return code;
    // Muy largo para la suite: se acorta con un resumen estable del resto.
    let h = 0;
    for (const ch of code)
        h = (Math.imul(h, 31) + ch.charCodeAt(0)) >>> 0;
    return `${code.slice(0, 71)}.${h.toString(36).padStart(7, '0').slice(0, 8)}`;
}
/**
 * La categoría (envío de la lista fija) de un paso que manda correo: «Solicitar información» es
 * `informacion.solicitud`; «Enviar al juzgado», `juzgado.comunicacion`; «Notificar al ciudadano», la de su plantilla.
 * Una respuesta es `respuesta.felicitacion` si la plantilla es la de felicitación o si todos los tipos de caso del flujo
 * son de felicitación o sugerencia; si no, `respuesta`. Al enviar, el caso decide con su tipo (`citizenMailFeature`).
 */
export function stepParentFeature(n, templateKind, caseTypes = []) {
    if (n.actionCode === 'REQUEST_INFORMATION')
        return MailFeatureCode.INFORMACION_SOLICITUD;
    if (n.actionCode === 'SEND_TO_COURT')
        return MailFeatureCode.JUZGADO_COMUNICACION;
    if (n.actionCode !== 'NOTIFY_CITIZEN')
        return null;
    const templateCode = n.config?.templateCode || DEFAULT_NOTIFY_TEMPLATE;
    const kind = templateKind(templateCode);
    const felicitation = caseTypes.length > 0 && caseTypes.every((t) => FELICITATION_CASE_TYPES.includes(t));
    return citizenMailFeature({ templateCode, templateKind: kind, caseTypeCode: felicitation ? caseTypes[0] : null });
}
/** El envío de un paso: «<Flujo> › <Paso>», agrupado por flujo, certificable si su categoría lo es. */
export function stepMailFeature(workflow, node, parent) {
    const category = SGDEA_MAIL_FEATURES.find((f) => f.code === parent);
    return {
        code: stepMailFeatureCode(workflow.code, node.id),
        name: `${workflow.name} › ${node.name || node.id}`.slice(0, 120),
        description: `Paso «${node.name || node.id}» del flujo «${workflow.name}». Sin ruta propia, sale como «${category.name}».`.slice(0, 300),
        group: workflow.name.slice(0, 60),
        certifiable: category.certifiable,
        parent,
    };
}
/** `GET /v1/communications/mail-route`: por dónde sale un envío (el de un paso, con su categoría). */
export const mailRouteQuerySchema = z.object({
    feature: z.string().min(1).max(80).regex(/^[a-z0-9][a-z0-9._-]*$/),
    fallback: z.enum(SGDEA_MAIL_FEATURES.map((f) => f.code)).optional(),
});
//# sourceMappingURL=mail-features.js.map